// #region IMPORT
import { IK_SELECTORS } from 'components/PoseMode/constants';
import { posingStore } from 'stores';
import { GizmoModeType, IKChainType } from 'stores/posingStore';

import type PosingSystem from '../PosingSystem';
// @ts-expect-error ts(7016) : Could not find a declaration file for module
import { Quat, Vec3 } from '../ossos/ossos.min';
import MachineBase from './MachineBase';

// #endregion

export default class FKRotMachine extends MachineBase {
  name = 'fkrotate';
  pSystem!: PosingSystem;
  chainKey!: string;

  // #region STATE MACHINE OVERRIDES
  onInit(ps: PosingSystem) {
    const sel = IK_SELECTORS[posingStore.state.activeIKChain];
    this.chainKey = sel.chainKey;
    this.pSystem = ps;
    this.initGizmo();
  }
  // #endregion

  // #region GIZMO EVENT OVERRIDES
  onGizmoDragStart(): void {
    this.pSystem.poser.selectors.clear();
  }

  onGizmoDragEnd(): void {
    this.pSystem.poser.refresh();
  }

  onGizmoRotate(v: Array<number>): void {
    const rig = this.pSystem.poser.rig;
    const chain = rig[this.chainKey].chain;
    const lnk = chain.links[0];

    const bone = rig.wpose.getBone(lnk.index); // Get final bone in the chain
    const tran = rig.wpose.getWorldTransform(lnk.pindex); // get parent bone's worldspace transform

    tran.rot.invert().mul(v); // convert rotation to local space :: invert( parent.rot ) * gizmo.rot
    bone.local.rot.copy(tran.rot);

    this.pSystem.poser.refreshSkeleton();
  }

  onGizmoModeChange(mode: string): void {
    // Lock specific rotation modes to certain chains
    const sel = IK_SELECTORS[posingStore.state.activeIKChain];
    switch (sel.type) {
      // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      case IKChainType.LeftToe:
      case IKChainType.RightToe:
        if (posingStore.state.gizmoMode !== GizmoModeType.xTwist) {
          posingStore.state.gizmoMode = GizmoModeType.xTwist;
        }
        break;

      // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      default: {
        if (posingStore.state.gizmoMode !== GizmoModeType.Rotate) {
          posingStore.state.gizmoMode = GizmoModeType.Rotate;
        }
        break;
      }
    }
  }
  // #endregion

  // #region HISTORY EVENT OVERRIDES
  onUndoRedo() {
    this.initGizmo();
  }
  // #endregion

  // #region MISC

  /** Initialize the gizmo's starting position & rotation */
  initGizmo() {
    const sel = IK_SELECTORS[posingStore.state.activeIKChain];

    // Get current position & rotation of the first bone of the chains
    const chain = this.pSystem.poser.rig[sel.chainKey].chain;
    const lnk = chain.links[0];
    const ctran = this.pSystem.poser.rig.wpose.getWorldTransform(lnk.index);

    switch (sel.type) {
      case IKChainType.LeftToe:
      case IKChainType.RightToe: {
        posingStore.state.gizmoMode = GizmoModeType.xTwist;

        const swing = new Vec3().fromQuat(ctran.rot, lnk.axes.swing);
        const twist = new Vec3().fromQuat(ctran.rot, lnk.axes.twist);

        const axes = Vec3.orthogonal(swing, twist);
        const rot = new Quat().fromLookDir(axes[2], axes[1]);

        this.pSystem.gizmo.resetTransform(ctran.pos, rot).show();
        break;
      }

      default: {
        posingStore.state.gizmoMode = GizmoModeType.Rotate;
        this.pSystem.gizmo.resetTransform(ctran.pos, ctran.rot).show();
      }
    }
  }

  // #endregion
}
